import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { commandsFor, groupCommands, isPaletteShortcut, searchCommands } from "@/lib/commandPalette";
import { cn } from "@/lib/utils";

/**
 * VX-2 universal command — one keyboard entry point (⌘K / Ctrl+K) to every
 * destination the signed-in role can already reach. Navigation only: route
 * guards and RLS stay the authority for what actually opens.
 */
export function UniversalCommand({ className }: { className?: string }) {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const items = useMemo(() => commandsFor(role), [role]);
  const results = useMemo(() => searchCommands(items, query, 20), [items, query]);
  const groups = useMemo(() => groupCommands(results), [results]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isPaletteShortcut(e)) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  if (items.length === 0) return null;

  const go = (to: string) => { setOpen(false); navigate(to); };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        aria-label="Open command palette"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={() => setOpen(true)}
        className={cn("h-9 justify-start gap-2 text-muted-foreground", className)}
      >
        <Search className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="ml-auto hidden rounded border border-border px-1.5 text-[10px] font-medium sm:inline">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Go to…"
          value={query}
          onValueChange={setQuery}
          aria-label="Search destinations"
        />
        <CommandList>
          <CommandEmpty>Nothing here matches that. Try a destination name.</CommandEmpty>
          {groups.map(({ group, items: groupItems }) => (
            <CommandGroup key={group} heading={group}>
              {groupItems.map((item) => (
                <CommandItem key={item.id} value={`${item.label} ${item.group}`} onSelect={() => go(item.to)}>
                  <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default UniversalCommand;
